import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import paymentService from '../services/paymentService';
import './CreditShop.css';

const CREDIT_PACKAGES = [
    { id: 'basic', credits: 5, price: 5.00, popular: false, bonus: 0 },
    { id: 'standard', credits: 12, price: 10.00, popular: true, bonus: 2 },
    { id: 'premium', credits: 30, price: 20.00, popular: false, bonus: 10 },
    { id: 'ultimate', credits: 75, price: 40.00, popular: false, bonus: 35 }
];

const CreditShop = ({ user, onPurchaseComplete }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('stripe');
    const [mbwayPhone, setMbwayPhone] = useState('');
    const [mbwayStatus, setMbwayStatus] = useState(null);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Check for success/cancel from URL
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('success')) {
            setSuccess(t('credits.purchaseSuccess'));
            if (onPurchaseComplete) onPurchaseComplete();
            // Clean URL
            window.history.replaceState({}, '', window.location.pathname);
        }
        if (urlParams.get('canceled')) {
            setError(t('credits.purchaseCanceled'));
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [t, onPurchaseComplete]);

    const handleStripeCheckout = async (pkg) => {
        setLoading(true);
        setError(null);
        
        try {
            const { sessionId } = await paymentService.createCheckoutSession(pkg, user.id);
            await paymentService.redirectToCheckout(sessionId);
        } catch (err) {
            setError(err.message || t('errors.paymentFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleMBWayPayment = async (pkg) => {
        if (!mbwayPhone || mbwayPhone.length < 9) {
            setError(t('validation.invalidPhone'));
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            const result = await paymentService.initiateMBWayPayment(mbwayPhone, pkg.price, user.id);
            setMbwayStatus({ ...result, packageId: pkg.id });
            
            // Poll for status
            const pollStatus = setInterval(async () => {
                try {
                    const status = await paymentService.checkMBWayStatus(result.paymentId);
                    if (status.status === 'completed') {
                        clearInterval(pollStatus);
                        setSuccess(t('credits.purchaseSuccess'));
                        setMbwayStatus(null);
                        if (onPurchaseComplete) onPurchaseComplete();
                    } else if (status.status === 'failed' || status.status === 'expired') {
                        clearInterval(pollStatus);
                        setError(t('errors.paymentFailed'));
                        setMbwayStatus(null);
                    }
                } catch (err) {
                    clearInterval(pollStatus);
                    setMbwayStatus(null);
                }
            }, 3000);

            // Stop polling after 5 minutes
            setTimeout(() => clearInterval(pollStatus), 300000);
        } catch (err) {
            setError(err.message || t('errors.paymentFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handlePurchase = (pkg) => {
        setSelectedPackage(pkg);
        if (paymentMethod === 'stripe') {
            handleStripeCheckout(pkg);
        } else if (paymentMethod === 'mbway') {
            handleMBWayPayment(pkg);
        }
    };

    return (
        <div className="credit-shop">
            <h2 className="shop-title">
                <span className="icon">💰</span>
                {t('credits.buyCredits')}
            </h2>

            {error && (
                <div className="alert alert-error">
                    <span className="icon">⚠️</span>
                    {error}
                    <button onClick={() => setError(null)}>×</button>
                </div>
            )}

            {success && (
                <div className="alert alert-success">
                    <span className="icon">✅</span>
                    {success}
                    <button onClick={() => setSuccess(null)}>×</button>
                </div>
            )}

            {/* Payment Method Selection */}
            <div className="payment-method-selector">
                <label className="method-label">{t('credits.paymentMethod')}:</label>
                <div className="method-options">
                    <button 
                        className={`method-btn ${paymentMethod === 'stripe' ? 'active' : ''}`}
                        onClick={() => setPaymentMethod('stripe')}
                    >
                        <span className="method-icon">💳</span>
                        <span>Cartão</span>
                    </button>
                    <button 
                        className={`method-btn ${paymentMethod === 'mbway' ? 'active' : ''}`}
                        onClick={() => setPaymentMethod('mbway')}
                    >
                        <span className="method-icon">📱</span>
                        <span>MB Way</span>
                    </button>
                </div>
            </div>

            {/* MB Way Phone Input */}
            {paymentMethod === 'mbway' && (
                <div className="mbway-phone-input">
                    <label>{t('credits.phoneNumber')}:</label>
                    <input
                        type="tel"
                        placeholder="912345678"
                        value={mbwayPhone}
                        onChange={(e) => setMbwayPhone(e.target.value.replace(/\D/g, ''))}
                        maxLength={9}
                    />
                </div>
            )}

            {/* MB Way Status */}
            {mbwayStatus && (
                <div className="mbway-status">
                    <div className="spinner"></div>
                    <p>{t('credits.mbwayWaiting')}</p>
                    <p className="phone-display">📱 {mbwayPhone}</p>
                </div>
            )}

            {/* Credit Packages */}
            <div className="packages-grid">
                {CREDIT_PACKAGES.map((pkg) => (
                    <div 
                        key={pkg.id} 
                        className={`package-card ${pkg.popular ? 'popular' : ''} ${selectedPackage?.id === pkg.id ? 'selected' : ''}`}
                    >
                        {pkg.popular && (
                            <div className="popular-badge">{t('credits.mostPopular')}</div>
                        )}
                        
                        <div className="package-credits">
                            <span className="credit-count">{pkg.credits}</span>
                            <span className="credit-label">{t('credits.credits')}</span>
                        </div>
                        
                        {pkg.bonus > 0 && (
                            <div className="bonus-badge">
                                +{pkg.bonus} {t('credits.bonus')}!
                            </div>
                        )}
                        
                        <div className="package-price">
                            <span className="currency">€</span>
                            <span className="amount">{pkg.price.toFixed(2)}</span>
                        </div>
                        
                        <div className="price-per-credit">
                            €{(pkg.price / pkg.credits).toFixed(2)} / {t('credits.credit')}
                        </div>
                        
                        <button
                            className="buy-btn"
                            onClick={() => handlePurchase(pkg)}
                            disabled={loading || mbwayStatus}
                        >
                            {loading && selectedPackage?.id === pkg.id ? (
                                <span className="loading-spinner"></span>
                            ) : (
                                <>
                                    {paymentMethod === 'stripe' ? '💳' : '📱'} {t('credits.buy')}
                                </>
                            )}
                        </button>
                    </div>
                ))}
            </div>

            {/* Security Notice */}
            <div className="security-notice">
                <span className="lock-icon">🔒</span>
                <span>{t('credits.securePayment')}</span>
            </div>
        </div>
    );
};

export default CreditShop;
